/**
 * Document Mask Utilities for CPF and CNPJ
 *
 * Provides automatic masking for Brazilian CPF (11 digits) and CNPJ (14 digits).
 * Formats dynamically as the user types, with optional validation.
 */

/**
 * Remove all non-digit characters from a string
 * @param value - The input string
 * @returns Only the numeric digits
 */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Format a string as CPF (000.000.000-00)
 * @param value - Numeric string with up to 11 digits
 * @returns Formatted CPF string
 */
export function formatCPF(value: string): string {
  // Limit to 11 digits
  const digits = value.slice(0, 11);

  // Apply CPF mask: 000.000.000-00
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')       // First dot after 3 digits
    .replace(/(\d{3})(\d)/, '$1.$2')       // Second dot after next 3 digits
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Dash before last 2 digits
}

/**
 * Format a string as CNPJ (00.000.000/0000-00)
 * @param value - Numeric string with up to 14 digits
 * @returns Formatted CNPJ string
 */
export function formatCNPJ(value: string): string {
  // Limit to 14 digits
  const digits = value.slice(0, 14);

  // Apply CNPJ mask: 00.000.000/0000-00
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')        // First dot after 2 digits
    .replace(/(\d{3})(\d)/, '$1.$2')        // Second dot after next 3 digits
    .replace(/(\d{3})(\d)/, '$1/$2')        // Slash after next 3 digits
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2'); // Dash before last 2 digits
}

/**
 * Auto-detect and format as CPF or CNPJ based on length
 * @param value - Input string (may contain non-numeric characters)
 * @returns Formatted CPF or CNPJ
 */
export function formatDocument(value: string): string {
  const digits = onlyDigits(value);

  // If 11 digits or less, format as CPF
  if (digits.length <= 11) {
    return formatCPF(digits);
  }

  // Otherwise, format as CNPJ
  return formatCNPJ(digits);
}
